import { ApiError } from '@clockwise/shared';

/**
 * Per-timesheet-entry field errors keyed by timesheet id. Set on submit
 * failure from `ValidationError.fields` (paths shaped
 * `entries[<id>].<field>`) and threaded into the grid so the offending
 * cells highlight inline — G12: errors next to the offending input, not
 * just a top-of-form toast.
 */
export type TimesheetSubmitFieldErrors = Record<
  string,
  {
    customerId?: string;
    productId?: string;
    engagementId?: string;
    hours?: string;
    description?: string;
  }
>;

const PATH = /^entries\[([^\]]+)\]\.(customerId|productId|engagementId|hours|description)$/;

/**
 * Parse `entries[<id>].<field>` paths out of an `ApiError.fields` envelope
 * into a per-id map. Anything else (top-level paths, unknown fields,
 * non-ApiError errors) returns `{}` — the caller falls back to its toast.
 *
 * Don't broaden the field whitelist without updating the grid's render —
 * any new key has to map to a visual treatment, otherwise the user sees
 * the toast but no inline hint, which is worse than the current state.
 */
export function parseSubmitFieldErrors(err: unknown): TimesheetSubmitFieldErrors {
  if (!(err instanceof ApiError) || !err.fields) return {};
  const out: TimesheetSubmitFieldErrors = {};
  for (const f of err.fields) {
    const m = PATH.exec(f.path);
    if (!m) continue;
    const [, id, field] = m;
    if (!id || !field) continue;
    let bucket = out[id];
    if (bucket === undefined) {
      bucket = {};
      out[id] = bucket;
    }
    if (field === 'customerId') bucket.customerId = f.message;
    else if (field === 'productId') bucket.productId = f.message;
    else if (field === 'engagementId') bucket.engagementId = f.message;
    else if (field === 'hours') bucket.hours = f.message;
    else if (field === 'description') bucket.description = f.message;
  }
  return out;
}
